import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CaretLeft, Calendar, MapPin, CloudSun, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "@/config/constants";
import { useAppContext } from "@/context/AppContext";

const PrintViewPage = () => {
  const navigate = useNavigate();
  const { t } = useAppContext();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/entries`);
        setEntries(response.data);
      } catch (error) { toast.error("Failed to load entries"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handlePrint = () => { window.print(); };

  if (loading) {
    return (<div className="max-w-4xl mx-auto px-4 py-8"><Skeleton className="h-10 w-64 mb-6 bg-[var(--bg-secondary)]" /></div>);
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="print-page">
      <div className="flex items-center justify-between mb-6 no-print">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-[var(--text-secondary)]"><CaretLeft size={20} />{t("back")}</Button>
        <Button onClick={handlePrint} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"><Printer size={20} className="mr-2" />{t("print")}</Button>
      </div>
      <div className="print-container">
        <div className="print-header text-center mb-8">
          <h1 className="font-heading text-4xl font-semibold text-[var(--text-primary)]">{t("appName")}</h1>
          <p className="text-[var(--text-muted)] mt-2">Generated on {new Date().toLocaleDateString()}</p>
        </div>
        {entries.map(entry => (
          <div key={entry.id} className="print-entry mb-8 border border-[var(--border-default)] rounded-lg p-6">
            <h2 className="font-heading text-2xl font-semibold text-[var(--text-primary)] mb-2">{entry.title}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-[var(--text-muted)] mb-4">
              <span className="flex items-center gap-1"><Calendar size={14} />{new Date(entry.created_at).toLocaleDateString()}</span>
              {entry.location && (<span className="flex items-center gap-1"><MapPin size={14} />{entry.location}</span>)}
              {entry.weather && (<span className="flex items-center gap-1"><CloudSun size={14} />{entry.weather}</span>)}
            </div>
            {entry.tags?.length > 0 && (<div className="flex flex-wrap gap-2 mb-4">{entry.tags.map(tag => (<Badge key={tag} className="bg-[var(--bg-secondary)] text-[var(--text-primary)]">{tag}</Badge>))}</div>)}
            <p className="text-[var(--text-primary)] whitespace-pre-wrap mb-4">{entry.content}</p>
            {entry.photos?.length > 0 && (<div className="grid grid-cols-2 gap-4 mb-4">{entry.photos.slice(0, 2).map(photo => (<img key={photo.id} src={`${API}/photos/${photo.storage_path}`} alt={entry.title} className="print-photo w-full h-40 object-cover rounded-lg" />))}</div>)}
            {entry.species_identified?.length > 0 && (
              <div className="bg-[var(--bg-secondary)] p-4 rounded-lg">
                <h3 className="font-semibold text-[var(--text-primary)] mb-2">{t("speciesIdentified")}</h3>
                <ul className="list-disc list-inside text-[var(--text-secondary)]">{entry.species_identified.map(sp => (<li key={sp.id}>{sp.species_name} ({sp.category}) - {(sp.confidence * 100).toFixed(0)}% {t("confidence").toLowerCase()}</li>))}</ul>
              </div>
            )}
            {entry.ai_insights && (<div className="mt-4 bg-[var(--bg-secondary)] p-4 rounded-lg"><h3 className="font-semibold text-[var(--text-primary)] mb-2">{t("aiInsights")}</h3><p className="text-sm text-[var(--text-secondary)]">{entry.ai_insights}</p></div>)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrintViewPage;
